import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Search, ShieldCheck, Users, Home, ArrowRight, CheckCircle } from 'lucide-react';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { Link } from 'react-router-dom';
export function HowItWorks() {
  const ref = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const steps = [{
    number: '01',
    icon: Search,
    title: 'Browse Verified Hostels',
    description: 'Search through 850+ physically verified properties near your campus. Every listing includes real photos and honest reviews.',
    color: 'from-blue-500 to-cyan-600',
    image: 'https://cdn.magicpatterns.com/uploads/goHkoTBNqivNWfS6sUUgPa/Copy_of_Billvast_Shirt_Mock-up_.png'
  }, {
    number: '02',
    icon: Users,
    title: 'Match with Roommates',
    description: 'Take our 2-minute Vibe Check quiz. Our AI pairs you with compatible roommates based on lifestyle and preferences.',
    color: 'from-brand-primary to-purple-600',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  }, {
    number: '03',
    icon: ShieldCheck,
    title: 'Verify & Pay Securely',
    description: 'Visit the property, verify everything matches the listing, then pay through our secure escrow system. Your money is protected.',
    color: 'from-emerald-500 to-green-600',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  }, {
    number: '04',
    icon: Home,
    title: 'Move In with Confidence',
    description: "Once verified, move into your new home knowing it's safe, legitimate, and exactly as advertised. Welcome home!",
    color: 'from-orange-500 to-red-600',
    image: 'https://images.unsplash.com/photo-1556912173-46c336c7fd55?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  }];
  return <Section ref={ref} className="bg-gradient-to-b from-slate-50 to-white relative overflow-hidden" data-id="element-191">
      {/* Animated Background */}
      <motion.div style={{
      y
    }} className="absolute top-0 right-0 w-96 h-96 bg-brand-primary/5 rounded-full blur-3xl" data-id="element-192" />

      <div className="relative z-10" data-id="element-193">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-194">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary/10 rounded-full text-brand-primary text-sm font-medium mb-6" data-id="element-195">
            <CheckCircle className="w-4 h-4" data-id="element-196" />
            <span data-id="element-197">Simple Process</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-4" data-id="element-198">
            How It <span className="text-brand-primary" data-id="element-199">Works</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto" data-id="element-200">
            From search to move-in, we've made finding safe student housing
            simple and stress-free.
          </p>
        </motion.div>

        <div className="space-y-24" data-id="element-201">
          {steps.map((step, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`} data-id="element-202">
              {/* Content */}
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`} data-id="element-203">
                <motion.div initial={{
              scale: 0
            }} whileInView={{
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1 + 0.2,
              type: 'spring'
            }} className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} text-white mb-6 shadow-lg`} data-id="element-204">
                  <step.icon className="w-8 h-8" data-id="element-205" />
                </motion.div>

                <motion.div initial={{
              opacity: 0,
              x: index % 2 === 0 ? -20 : 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1 + 0.3
            }} className="mb-4" data-id="element-206">
                  <span className={`text-6xl font-bold bg-gradient-to-br ${step.color} bg-clip-text text-transparent opacity-20`} data-id="element-207">
                    {step.number}
                  </span>
                </motion.div>

                <motion.h3 initial={{
              opacity: 0,
              x: index % 2 === 0 ? -20 : 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1 + 0.4
            }} className="text-3xl font-bold text-brand-dark mb-4" data-id="element-208">
                  {step.title}
                </motion.h3>

                <motion.p initial={{
              opacity: 0,
              x: index % 2 === 0 ? -20 : 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1 + 0.5
            }} className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-209">
                  {step.description}
                </motion.p>

                <motion.div initial={{
              opacity: 0,
              x: index % 2 === 0 ? -20 : 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1 + 0.6
            }} data-id="element-210">
                  <Link to={index === 0 ? '/find-hostel' : index === 1 ? '/roommate-match' : '/signup'} data-id="element-211">
                    <Button variant="outline" className="group" data-id="element-212">
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-213" />
                    </Button>
                  </Link>
                </motion.div>
              </div>

              {/* Image */}
              <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1 + 0.3,
            duration: 0.6
          }} className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`} data-id="element-214">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-215">
                  <motion.img src={step.image} alt={step.title} className="w-full h-96 object-cover" whileHover={{
                scale: 1.05
              }} transition={{
                duration: 0.4
              }} data-id="element-216" />
                  <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-20`} data-id="element-217" />

                  {/* Floating Badge */}
                  <motion.div initial={{
                y: 20,
                opacity: 0
              }} whileInView={{
                y: 0,
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1 + 0.7
              }} className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg" data-id="element-218">
                    <div className="flex items-center gap-3" data-id="element-219">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center`} data-id="element-220">
                        <step.icon className="w-5 h-5 text-white" data-id="element-221" />
                      </div>
                      <div data-id="element-222">
                        <div className="font-bold text-brand-dark" data-id="element-223">
                          Step {step.number}
                        </div>
                        <div className="text-sm text-slate-600" data-id="element-224">
                          {step.title}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Decorative Elements */}
                <motion.div animate={{
              y: [0, -10, 0],
              rotate: [0, 5, 0]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className={`absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gradient-to-br ${step.color} opacity-20 blur-xl`} data-id="element-225" />
                <motion.div animate={{
              y: [0, 10, 0],
              rotate: [0, -5, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }} className={`absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-gradient-to-br ${step.color} opacity-20 blur-xl`} data-id="element-226" />
              </motion.div>
            </motion.div>)}
        </div>

        {/* CTA Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="mt-24 text-center" data-id="element-227">
          <div className="bg-gradient-to-br from-brand-dark to-brand-dark-secondary rounded-3xl p-12 relative overflow-hidden" data-id="element-228">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(circle, #b583c9 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} data-id="element-229"></div>
            <div className="relative z-10" data-id="element-230">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-231">
                Ready to Find Your Perfect Home?
              </h3>
              <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto" data-id="element-232">
                Join 5,000+ students who've found safe, verified housing through
                CampusAparte.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-233">
                <Link to="/find-hostel" data-id="element-234">
                  <Button size="lg" className="bg-brand-primary hover:bg-brand-light text-white" data-id="element-235">
                    Browse Hostels
                    <ArrowRight className="ml-2 w-5 h-5" data-id="element-236" />
                  </Button>
                </Link>
                <Link to="/roommate-match" data-id="element-237">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-id="element-238">
                    Find Roommates
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>;
}